import { useMutation } from '@tanstack/react-query';
import { useToast } from '../contextManager/toast.context'; // Assuming you have this
import type { Stripe, StripeElements } from '@stripe/stripe-js';
import {AxiosContext} from '../contextManager/axios.context';
import { endpoints } from '../constants/endpoints'; // NEW
import { useContext } from 'react'; // NEW
import {CalendarEvent} from './useEvents';

// Define the arguments our mutation function will need from the component
interface ConfirmPaymentPayload {
  stripe: Stripe | null;
  elements: StripeElements | null;
  clientSecret: string;
}

interface CreatePaymentIntentPayload {
    amount: number;
    metadata: {
      lessonIds: string;
    };
  }
type NewEventPayload = Omit<CalendarEvent, 'id' | 'createdAt'>;


interface CreateBookingAndPaymentIntent {
    amount: number;
}

export const usePayment = () => {
  const { addToast, updateToast } = useToast();

  const axios = useContext(AxiosContext);

  const {mutateAsync: createPaymentIntent, isPending: isCreatingIntent} = useMutation({
    mutationFn: async (payload: CreatePaymentIntentPayload) => {
        const response = await axios.post(endpoints.createPaymentIntent, payload);
        return response.data;
    },
    onMutate: () => {
        const toastId = `payment-intent-${Date.now()}`;
        addToast({ id: toastId, variant: 'loading', title: 'Initializing payment...' });
        return { toastId };
    },
    onSuccess: (_, __, context) => {
        if (context) updateToast(context.toastId, { variant: 'success', title: 'Payment ready.' });
    },
    onError: (error, _, context) => {
        console.error("Failed to create payment intent:", error);
        if (context) updateToast(context.toastId, { variant: 'error', title: 'Could not initialize payment' });
    }
  })

  const {mutateAsync: prepareBooking, isPending: isPreparingBooking} = useMutation({
    mutationFn: async (payload: CreateBookingAndPaymentIntent) => {
        const response = await axios.post(endpoints.createBookingAndPaymentIntent, payload);
        return response.data;
    },
    onMutate: () => {
        const toastId = `prepare-booking-${Date.now()}`;
        addToast({ id: toastId, variant: 'loading', title: 'Preparing booking...' });
        return { toastId };
    },
    onSuccess: (_, __, context) => {
        if (context) updateToast(context.toastId, { variant: 'success', title: 'Booking ready.' });
    },
    onError: (error, _, context) => {
        console.error('Failed to create booking: ', error);
        if (context) updateToast(context.toastId, { variant: 'error', title: 'Could not initialise booking' });
    }
  })


  const { mutateAsync: confirmCardPayment, isPending: isProcessing } = useMutation({
    mutationFn: async (payload: ConfirmPaymentPayload) => {
      const { stripe, elements, clientSecret } = payload;

      if (!stripe || !elements) {
        throw new Error('Stripe has not loaded yet. Please wait and try again.');
      }

      const { error: submitError } = await elements.submit();
      if (submitError) {
        console.error('Stripe elements.submit() error:', submitError);
        throw new Error(submitError.message || 'Form validation failed.');
      }

      // We save the card (SetupIntent) rather than charging now — each lesson is
      // charged off-session later, when the tutor marks it complete.
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret: clientSecret!,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (error) {
        console.error('Stripe confirmSetup error:', error);
        throw new Error(error.message || 'An unexpected error occurred saving your card.');
      }

      return setupIntent;
    },

    onMutate: () => {
      const toastId = `payment-${Date.now()}`;
      addToast({ id: toastId, variant: 'loading', title: 'Saving your card...' });
      return { toastId };
    },

    onSuccess: (_, __, context) => {
      if (context) updateToast(context.toastId, { variant: 'success', title: 'Card saved!' });
    },

    onError: (_error: Error, __, context) => {
      if (context) updateToast(context.toastId, { variant: 'error', title: 'Could not save card' });
    },
  });

  return {
    createPaymentIntent, 
    isCreatingIntent,
    confirmCardPayment, // The async function to call on form submit
    isProcessing,       // The loading state for the button
    isPreparingBooking, 
    prepareBooking,
  };
};

